import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { CircleQuestionMark } from '@repo/media/icons';

import { ButtonLink } from '@repo/components/button-link';
import { Card } from '@repo/components/card';
import { Heading } from '@repo/components/heading';
import { LoadingIndicator } from '@repo/components/loading-indicator';

import type { Route as AppRoute } from '~/config/routes.ts';
import { routesByPath } from '~/config/routes.ts';
import { FeatureGuard } from '~/features/auth/feature-guard.tsx';
import { useFeatureGuard } from '~/features/auth/use-feature-guard.ts';
import { ContentHeader } from '~/features/content-header/content-header.tsx';
import { queryOrganizationById, queryProjects } from '~/services/authorization/authorization.ts';

type NavCategory = {
  title: string;
  routes: AppRoute[];
};

const categories: NavCategory[] = [
  {
    title: 'Organization',
    routes: [
      routesByPath['/organization/$org/user-mgmt'],
    ],
  },
];

export const Route = createFileRoute('/organization/$org/')({
  loader: async ({ context: { queryClient }, params: { org } }) => {
    return Promise.all([
      queryClient.ensureQueryData(queryProjects(org)),
      queryClient.ensureQueryData(queryOrganizationById(org)),
    ]);
  },
  component: OrganizationRoot,
});

function OrganizationRoot() {
  const { org } = Route.useParams();
  const projects = useSuspenseQuery(queryProjects(org)).data;
  const organization = useSuspenseQuery(queryOrganizationById(org)).data;
  const { hasSomeFeature, isLoading: isFeatureGuardLoading } = useFeatureGuard();
  const visibleCategories = categories
    .map(category => ({
      ...category,
      routes: category.routes.filter(route => hasSomeFeature(route.features)),
    }))
    .filter(category => category.routes.length > 0);

  return (
    <div className="@container flex flex-col items-start gap-8 pb-6">
      <div className="flex flex-col gap-2">
        <ContentHeader>
          Organization overview
        </ContentHeader>
        <p>
          All your sites in
          {' '}
          {organization.name}
        </p>
      </div>

      {isFeatureGuardLoading
        ? (
            <div className="w-full min-h-32 flex items-center justify-center">
              <LoadingIndicator size={24} className="text-primary-700" />
            </div>
          )
        : (
            <div className="grid @4xl:grid-cols-2 gap-6 w-full">
              {visibleCategories.map(category => (
                <Card key={category.title}>
                  <Heading className="font-semibold text-base">{category.title}</Heading>
                  <div className="flex flex-col gap-2">
                    {category.routes.map(({ icon: Icon = CircleQuestionMark, ...route }) => (
                      <FeatureGuard key={route.path} features={route.features}>
                        <ButtonLink
                          to={route.path}
                          params={{ org }}
                          icon={{ component: <Icon size={20} strokeWidth={1.5} aria-label={route.title} /> }}
                          variant="ghost"
                          color="secondary"
                          size="sm"
                          className="justify-start"
                        >
                          {route.title}
                        </ButtonLink>
                      </FeatureGuard>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}

      <div className="flex flex-col gap-2 w-full">
        <Heading className="font-semibold text-base">
          Sites (
          {projects.length}
          )
        </Heading>
        <ul className="grid @2xl:grid-cols-2 @5xl:grid-cols-3 gap-6 w-full">
          {projects.length === 0 && (
            <li className="text-secondary-500">
              You do not have access to any sites yet.
            </li>
          )}
          {projects.map(project => (
            <li key={project.id}>
              <Card className="transition">
                <div className="flex justify-between items-center gap-6">
                  <div className="flex flex-col">
                    <p className="text-base font-semibold">{project.name}</p>
                    <p className="text-sm text-secondary-500">Site dashboard</p>
                  </div>
                  <ButtonLink
                    to="/project/$project"
                    params={{ project: project.id }}
                    variant="soft"
                    size="sm"
                  >
                    Open
                  </ButtonLink>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
